/**
 * Helpers de lecture du champ fact_id_5000 des opérations 8000 — COMPTA-219.
 *
 * fact_id_5000 n'est pas un identifiant unique mais une liste d'écritures 5000
 * séparées par ", " : une même facture peut couvrir plusieurs ventes, et une même
 * vente être rapprochée de plusieurs écritures. Des données historiques portent en
 * plus un préfixe ", " hérité d'un bug de concaténation côté storehouse.
 */

/** Découpe fact_id_5000 en identifiants, en ignorant les entrées vides. */
export function parseFactIdList(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
}

/**
 * Premier identifiant d'écriture 5000 d'une opération, ou "" si elle n'est pas pointée.
 * À utiliser partout où l'API attend un income_id / spending_id unique : transmettre
 * la chaîne brute ne correspondait à aucune écriture côté back.
 */
export function primaryFactId(raw) {
    const [first = ''] = parseFactIdList(raw);
    return first;
}

/**
 * Montant qu'un dépointage doit recréditer à l'écriture 5000 : le TTC, puisque
 * c'est ce que le pointage a débité (le montant à pointer est pré-rempli avec
 * total_available, initialisé au TTC à la création de l'opération). Repli sur le
 * HT pour les opérations antérieures à l'introduction du TTC.
 */
export function linkedAmount(operation) {
    if (!operation) return 0;
    const ttc = parseFloat(operation.total_price_TTC);
    if (Number.isFinite(ttc) && ttc > 0) return ttc;
    return parseFloat(operation.total_price) || 0;
}

/**
 * Montant rapproché d'une écriture comptable, déduit de son reste à pointer.
 *
 * Sert aux deux sens du rapprochement, qui portent des noms de champs différents :
 * recettes (income_amount / income_total_available, rapprochées avec les ventes) et
 * dépenses (spending_amount / spending_rest_to_match, rapprochées avec les achats et
 * les réapprovisionnements externes).
 *
 * Renvoie null si l'un des deux montants est inexploitable : présenter le montant total
 * comme rapproché sur une écriture qui ne l'est pas serait trompeur.
 */
export function reconciledAmount(total, remaining) {
    const amount = parseFloat(total);
    const rest = parseFloat(remaining);
    if (!Number.isFinite(amount) || !Number.isFinite(rest)) return null;
    return amount - rest;
}

/**
 * Montant déjà rapproché avec la compta 5000, déduit du reste à pointer (total_available).
 *
 * Renvoie null lorsque le reste à pointer est incohérent avec le montant de l'opération —
 * négatif, ou supérieur au total. Ces cas existent en base : le solde était décrémenté même
 * quand le rapprochement échouait côté 8000. Afficher un montant calculé sur une valeur
 * fausse serait pire que de ne rien afficher.
 *
 * Une opération marquée rapprochée l'est pour la totalité de son montant, quel que soit
 * l'état de total_available.
 */
export function pointedAmount(operation) {
    if (!operation) return null;
    const total = matchTotal(operation);
    if (!total) return null;
    if (isOperationMatched(operation)) return total;

    const remaining = remainingAmount(operation);
    if (remaining === null) return null;
    return total - remaining;
}

/** Vrai si l'opération est intégralement rapprochée, quel que soit le modèle de données. */
export function isOperationMatched(operation) {
    if (!operation) return false;
    // matched : modèle TransferCB actuel ; sale_matched : anciennes ventes (typesNew).
    return Boolean(operation.matched || operation.sale_matched);
}

/**
 * Montant de référence du rapprochement, celui auquel la somme des montants pointés
 * est comparée côté storehouse.
 *
 * Ventes et avoirs : le TTC (voir linkedAmount). Achats et réappros externes :
 * prix_revient, que matchPurchase.go:67 prend comme base et que la création initialise
 * au TTC. Les deux valeurs coïncident sur les opérations récentes, mais seul prix_revient
 * reproduit fidèlement le calcul du back sur les achats anciens.
 */
export function matchTotal(operation) {
    if (!operation) return 0;
    const isSale = operation.transfer_type === 'vente' || operation.transfer_type === 'avoir'
        || operation.nature === 'vente' || operation.nature === 'avoir';
    if (!isSale) {
        const costPrice = parseFloat(operation.prix_revient);
        if (Number.isFinite(costPrice) && costPrice > 0) return costPrice;
    }
    return linkedAmount(operation);
}

/** États de rapprochement d'une opération avec la comptabilité 5000. */
export const MATCHING_NONE = 'none';
export const MATCHING_PARTIAL = 'partial';
export const MATCHING_FULL = 'full';
export const MATCHING_INCONSISTENT = 'inconsistent';

/**
 * Reste à pointer d'une opération, ou null s'il est inexploitable.
 *
 * total_available est la seule source du reste : il est décrémenté par
 * updateSaleMatchAmount / updatePurchaseMatchAmount à chaque pointage. Un reste négatif
 * ou supérieur au montant de l'opération n'est pas une valeur à afficher mais le signe
 * d'un document incohérent.
 */
export function remainingAmount(operation) {
    if (!operation) return null;
    const total = matchTotal(operation);
    if (!total) return null;
    const remaining = parseFloat(rawRemaining(operation));
    if (!Number.isFinite(remaining) || remaining < 0 || remaining > total) return null;
    return remaining;
}

/**
 * Reste à pointer brut, quel que soit le modèle qui a sérialisé l'opération.
 *
 * total_available est la forme actuelle (TransferCB). Les modèles hérités nomment le même
 * champ purchase_total_available ou sale_total_available (common/types/typesCB.go,
 * typesNew.go) ; LinkPurchasePopUp.jsx:33 lit déjà les trois formes sur le même store.
 * Aucune opération de production ne porte les formes héritées au 2026-09-16, mais les lire
 * ne coûte rien et évite qu'un écran les présente un jour comme incohérentes.
 */
function rawRemaining(operation) {
    const candidates = [operation.total_available, operation.purchase_total_available, operation.sale_total_available];
    const value = candidates.find((candidate) => candidate !== undefined && candidate !== null);
    // Les trois champs portent omitempty : absents, ils valent zéro. Une opération marquée
    // rapprochée est déjà traitée en amont ; ici l'absence ne se distingue pas d'un reste nul,
    // et lire zéro est la seule interprétation fidèle au modèle.
    return value === undefined ? 0 : value;
}

/**
 * État réel du rapprochement d'une opération.
 *
 * fact_id_5000 ne suffit pas à conclure qu'une opération est pointée : il est renseigné
 * dès le premier rattachement, y compris partiel, et des états hérités le portent sans
 * que le reste à pointer ait été décrémenté (COMPTA-219). C'est total_available, et
 * matched quand il est posé, qui font foi.
 *
 * MATCHING_INCONSISTENT signale les documents qu'aucun écran ne doit présenter comme
 * pointés ni comme vierges : référence 5000 renseignée alors que rien n'a été décompté,
 * ou reste hors des bornes du montant de l'opération.
 */
export function matchingState(operation) {
    if (!operation) return MATCHING_NONE;
    if (isOperationMatched(operation)) return MATCHING_FULL;

    const hasFactID = parseFactIdList(operation.fact_id_5000).length > 0;
    const remaining = remainingAmount(operation);
    if (remaining === null) {
        // Sans référence 5000, un reste inexploitable ne dit rien de plus que "non pointé" :
        // seules les opérations rattachées méritent d'être signalées comme incohérentes.
        return hasFactID ? MATCHING_INCONSISTENT : MATCHING_NONE;
    }
    if (remaining === 0) {
        // Les champs de rapprochement portent omitempty : absents, ils se lisent comme un zéro.
        // 26 631 opérations jamais pointées sont dans ce cas en production — sans référence 5000,
        // un reste nul ne veut pas dire soldé, il veut dire que le champ n'a jamais été écrit.
        return hasFactID ? MATCHING_FULL : MATCHING_NONE;
    }
    if (remaining === matchTotal(operation)) {
        return hasFactID ? MATCHING_INCONSISTENT : MATCHING_NONE;
    }
    return MATCHING_PARTIAL;
}
